import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUp, ArrowDown, GripVertical, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * ReportGrid - High-performance data grid with virtual scrolling
 * Supports 100k+ rows, sticky headers, column resize, and horizontal scroll
 */
const ReportGrid = ({
  columns = [],
  data = [],
  theme = 'light',
  loading = false,
  onRowClick = null,
  selectedRow = null,
  sortColumn = null,
  sortDirection = 'asc',
  onSort = null,
  onColumnResize = null,
  rowHeight = 40,
  headerHeight = 44,
  emptyMessage = 'No data found',
  emptyIcon = null,
  showRowNumbers = false,
  className = '',
  // Pagination props
  pagination = null, // { page, pageSize, total, totalPages }
  onPageChange = null,
  onPageSizeChange = null,
  pageSizeOptions = [10, 20, 50, 100]
}) => {
  const isDark = theme === 'dark';
  const parentRef = useRef(null);
  const [resizing, setResizing] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});

  // Get visible columns only
  const visibleColumns = useMemo(() => 
    columns.filter(col => col.visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [columns]
  );

  // Calculate total width
  const totalWidth = useMemo(() => {
    let width = showRowNumbers ? 50 : 0;
    visibleColumns.forEach(col => {
      width += columnWidths[col.field] || col.width || 150;
    });
    return width;
  }, [visibleColumns, columnWidths, showRowNumbers]);

  // Virtual row renderer
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 20
  });

  // Handle column resize
  const handleResizeStart = useCallback((e, column) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ column, startX: e.clientX, startWidth: columnWidths[column.field] || column.width || 150 });
  }, [columnWidths]);

  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    const diff = e.clientX - resizing.startX;
    const newWidth = Math.max(resizing.column.minWidth || 50, Math.min(resizing.column.maxWidth || 500, resizing.startWidth + diff));
    setColumnWidths(prev => ({ ...prev, [resizing.column.field]: newWidth }));
  }, [resizing]);

  const handleResizeEnd = useCallback(() => {
    if (resizing && onColumnResize) {
      onColumnResize(resizing.column.field, columnWidths[resizing.column.field]);
    }
    setResizing(null);
  }, [resizing, columnWidths, onColumnResize]);

  // Format cell value
  const formatValue = (value, column) => {
    if (value === null || value === undefined) return '-';
    
    switch (column.dataType) {
      case 'currency':
        return new Intl.NumberFormat('en-IN', { 
          style: 'currency', 
          currency: 'INR',
          minimumFractionDigits: 2 
        }).format(value);
      case 'number':
        return new Intl.NumberFormat('en-IN').format(value);
      case 'date':
        if (typeof value === 'string') return value;
        return new Date(value).toLocaleDateString('en-IN');
      case 'percentage':
        return `${value}%`;
      default:
        return String(value);
    }
  };

  // Get cell alignment class
  const getAlignClass = (align) => {
    switch (align) {
      case 'right': return 'text-right justify-end';
      case 'center': return 'text-center justify-center';
      default: return 'text-left justify-start';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Loading data...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className="flex flex-col items-center gap-3">
          {emptyIcon}
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex-1 flex flex-col overflow-hidden border rounded-lg min-h-0 ${isDark ? 'border-[#222] bg-[#111]' : 'border-gray-200 bg-white'} ${className}`}
      onMouseMove={resizing ? handleResizeMove : undefined}
      onMouseUp={resizing ? handleResizeEnd : undefined}
      onMouseLeave={resizing ? handleResizeEnd : undefined}
      data-testid="report-grid"
    >
      {/* Sticky Header */}
      <div 
        className={`shrink-0 overflow-hidden ${isDark ? 'bg-[#0e0e10]' : 'bg-gray-50'}`}
        style={{ height: headerHeight }}
      >
        <div className="flex h-full" style={{ width: totalWidth, minWidth: '100%' }}>
          {/* Row number header */}
          {showRowNumbers && (
            <div 
              className={`shrink-0 flex items-center justify-center border-r border-b font-semibold text-xs uppercase ${isDark ? 'border-[#222] text-slate-500' : 'border-gray-200 text-gray-500'}`}
              style={{ width: 50 }}
            >
              #
            </div>
          )}
          
          {/* Column headers */}
          {visibleColumns.map((column, colIndex) => {
            const width = columnWidths[column.field] || column.width || 150;
            const isSorted = sortColumn === column.field;
            const canSort = column.sortable !== false && onSort;
            
            return (
              <div
                key={column.field}
                className={`shrink-0 flex items-center gap-2 px-3 border-r border-b relative group select-none ${isDark ? 'border-[#222]' : 'border-gray-200'} ${canSort ? 'cursor-pointer hover:bg-black/5' : ''}`}
                style={{ width }}
                onClick={() => canSort && onSort(column.field)}
                data-testid={`grid-header-${column.field}`}
              >
                <span className={`flex-1 truncate font-semibold text-xs uppercase ${getAlignClass(column.align)} ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  {column.header}
                </span>
                
                {/* Sort indicator */}
                {isSorted && (
                  <span className="shrink-0">
                    {sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                  </span>
                )}
                
                {/* Resize handle */}
                <div
                  className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'bg-blue-500' : 'bg-blue-400'}`}
                  onMouseDown={(e) => handleResizeStart(e, column)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Scrollable Body with Virtual Rows */}
      <div 
        ref={parentRef}
        className="flex-1 overflow-auto min-h-0"
        style={{ contain: 'strict' }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: totalWidth,
            minWidth: '100%',
            position: 'relative'
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = data[virtualRow.index];
            const isSelected = selectedRow === virtualRow.index;
            
            return (
              <div
                key={virtualRow.index}
                data-testid={`grid-row-${virtualRow.index}`}
                className={`absolute top-0 left-0 flex border-b transition-colors ${
                  isSelected 
                    ? isDark ? 'bg-blue-500/20' : 'bg-blue-50'
                    : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                } ${isDark ? 'border-[#1a1a1a]' : 'border-gray-100'} ${onRowClick ? 'cursor-pointer' : ''}`}
                style={{
                  height: rowHeight,
                  transform: `translateY(${virtualRow.start}px)`,
                  width: totalWidth
                }}
                onClick={() => onRowClick && onRowClick(row, virtualRow.index)}
              >
                {/* Row number */}
                {showRowNumbers && (
                  <div 
                    className={`shrink-0 flex items-center justify-center border-r text-xs ${isDark ? 'border-[#222] text-slate-600' : 'border-gray-100 text-gray-400'}`}
                    style={{ width: 50 }}
                  >
                    {virtualRow.index + 1}
                  </div>
                )}
                
                {/* Data cells */}
                {visibleColumns.map((column) => {
                  const width = columnWidths[column.field] || column.width || 150;
                  const value = row[column.field];
                  
                  return (
                    <div
                      key={column.field}
                      className={`shrink-0 flex items-center px-3 border-r truncate ${isDark ? 'border-[#1a1a1a]' : 'border-gray-50'} ${getAlignClass(column.align)}`}
                      style={{ width }}
                      title={column.render ? undefined : String(value || '')}
                    >
                      {column.render ? column.render(value, row, virtualRow.index) : (
                        <span className={`truncate text-sm ${
                          column.dataType === 'currency' && value < 0 ? 'text-red-500' :
                          column.dataType === 'currency' && value > 0 && column.field.toLowerCase().includes('cr') ? 'text-red-500' :
                          column.dataType === 'currency' && value > 0 && column.field.toLowerCase().includes('dr') ? 'text-green-500' :
                          isDark ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                          {formatValue(value, column)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer with pagination or row count */}
      <div className={`shrink-0 px-4 py-2 border-t text-xs flex items-center justify-between ${isDark ? 'border-[#222] bg-[#0e0e10] text-slate-500' : 'border-gray-200 bg-gray-50 text-gray-500'}`}
        data-testid="report-grid-footer"
      >
        {pagination ? (
          <>
            {/* Left: Row info */}
            <div className="flex items-center gap-2" data-testid="pagination-info">
              <span>
                {pagination.total > 0
                  ? `${((pagination.page - 1) * pagination.pageSize) + 1}–${Math.min(pagination.page * pagination.pageSize, pagination.total)} of ${pagination.total.toLocaleString()}`
                  : '0 rows'}
              </span>
            </div>

            {/* Center: Page navigation */}
            <div className="flex items-center gap-1" data-testid="pagination-nav">
              <button
                onClick={() => onPageChange?.(1)}
                disabled={pagination.page <= 1}
                className={`p-1 rounded transition-colors ${pagination.page <= 1 ? 'opacity-30 cursor-not-allowed' : isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                title="First page"
                data-testid="pagination-first"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => onPageChange?.(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className={`p-1 rounded transition-colors ${pagination.page <= 1 ? 'opacity-30 cursor-not-allowed' : isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                title="Previous page"
                data-testid="pagination-prev"
              >
                <ChevronLeft size={16} />
              </button>
              <span className={`px-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`} data-testid="pagination-page-display">
                Page {pagination.page} of {pagination.totalPages || 1}
              </span>
              <button
                onClick={() => onPageChange?.(pagination.page + 1)}
                disabled={pagination.page >= (pagination.totalPages || 1)}
                className={`p-1 rounded transition-colors ${pagination.page >= (pagination.totalPages || 1) ? 'opacity-30 cursor-not-allowed' : isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                title="Next page"
                data-testid="pagination-next"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => onPageChange?.(pagination.totalPages || 1)}
                disabled={pagination.page >= (pagination.totalPages || 1)}
                className={`p-1 rounded transition-colors ${pagination.page >= (pagination.totalPages || 1) ? 'opacity-30 cursor-not-allowed' : isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                title="Last page"
                data-testid="pagination-last"
              >
                <ChevronsRight size={16} />
              </button>
            </div>

            {/* Right: Page size selector */}
            <div className="flex items-center gap-2" data-testid="pagination-size-selector">
              <span>Rows:</span>
              <select
                value={pagination.pageSize}
                onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
                className={`px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#151515] border-[#333] text-slate-300' : 'bg-white border-gray-200 text-gray-700'}`}
                data-testid="pagination-page-size"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <span>Showing {data.length.toLocaleString()} rows</span>
        )}
      </div>
    </div>
  );
};

export default ReportGrid;
